import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { CloseIcon } from '@features/shared/icons/icons'
import SidePanelResizeHandle from '@features/shared/components/SidePanelResizeHandle'
import { IconButton } from '@features/shared/components/IconButton'

import { FormRequirements } from '@features/shared/components/FormRequirements'
/** Shared opening width so the chat and support panels match side by side. */
const SIDE_PANEL_DEFAULT_WIDTH_PX = 400

interface SidePanelShellProps {
  /** Its one title, which also names it (and its resize handle) for assistive tech. */
  title: string
  /** A line under the title, such as what happens after a form is sent. */
  subtitle?: string
  /** Panel-owned actions, before Close. */
  actions?: ReactNode
  onClose: () => void
  /** An inner panel view can use Escape to return before the panel closes. */
  onEscape?: () => void
  minWidthPx: number
  maxWidthPx: number
  children: ReactNode
  bodyClassName?: string
}

/** A resizable side panel with a drag handle on its left edge and a close button. */
export default function SidePanelShell({
  title,
  subtitle,
  actions,
  onClose,
  onEscape,
  minWidthPx,
  maxWidthPx,
  children,
  bodyClassName = 'flex-1 overflow-y-auto',
}: SidePanelShellProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement | null>(null)
  const [panelWidth, setPanelWidth] = useState(SIDE_PANEL_DEFAULT_WIDTH_PX)
  const [isResizing, setIsResizing] = useState(false)

  // Opening moves focus into the panel, to its close button, unless something
  // in it took focus first (the AI Assistant's message box).
  useEffect(() => {
    if (!panelRef.current?.contains(document.activeElement)) closeButtonRef.current?.focus()
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    // An Escape a control inside used (closing its open list) isn't the panel's.
    if (e.key === 'Escape' && !e.defaultPrevented) {
      e.stopPropagation()
      const dismiss = onEscape ?? onClose
      dismiss()
    }
  }

  const handleResizeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return

    const startWidth =
      panelRef.current?.getBoundingClientRect().width ?? SIDE_PANEL_DEFAULT_WIDTH_PX
    const startX = event.clientX
    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect

    event.preventDefault()
    setIsResizing(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const viewportMaxWidth = Math.floor(window.innerWidth * 0.5)
      const maxWidth = Math.max(minWidthPx, Math.min(maxWidthPx, viewportMaxWidth))
      const nextWidth = startWidth + (startX - moveEvent.clientX)
      setPanelWidth(Math.min(maxWidth, Math.max(minWidthPx, nextWidth)))
    }

    const handlePointerUp = () => {
      setIsResizing(false)
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
  }

  return (
    <aside
      ref={panelRef}
      className="app-frame-panel-end relative flex shrink-0 flex-col surface-default"
      aria-label={title}
      onKeyDown={handleKeyDown}
      style={{ width: panelWidth }}
    >
      <SidePanelResizeHandle
        edge="start"
        label={`Resize ${title}`}
        isResizing={isResizing}
        onPointerDown={handleResizeStart}
        layer="overlay"
      />
      <div className="box-content flex h-[var(--page-header-height)] shrink-0 items-center justify-between rounded-t-[inherit] border-b border-default bg-[color:var(--surface-sidebar-submenu-chrome)] px-5">
        <div className="min-w-0 flex-1 pr-2">
          <h2 className="truncate text-sm font-semibold leading-5 text-default">{title}</h2>
          {subtitle ? <p className="truncate text-xs leading-4 text-muted">{subtitle}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          <IconButton
            ref={closeButtonRef}
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-icon-quiet transition-colors hover:bg-muted hover:text-data"
            onClick={onClose}
            label="Close panel"
          >
            <CloseIcon size={14} />
          </IconButton>
        </div>
      </div>
      <div className={`${bodyClassName} rounded-b-[inherit]`}>
        {/* A side panel is a form: its fields' requirement marks are decided together. */}
        <FormRequirements>{children}</FormRequirements>
      </div>
    </aside>
  )
}
